import React, { Component } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import "../../assets/css/argon-dashboard-react.css"; // Import custom CSS to hide CanvasJS logo and position the dropdown

var CanvasJSChart = CanvasJSReact.CanvasJSChart;

class TokenChart extends Component {
  constructor() {
    super();
    this.state = {
      selectedOption: "last7Days", // Default selection
      dataPoints: [], // Empty data initially
      tokenSales: {
        daily: [],
        weekly: [],
        monthly: [],
      },
    };
  }

  // Method to fetch data from API
  fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const tokenSales = data?.stats?.transactions?.tokenSales || {
        daily: [],
        weekly: [],
        monthly: [],
      };

      this.setState({ tokenSales }, () => this.processData(tokenSales));
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  // Method to process data based on selected range
  processData = (tokenSales = this.state.tokenSales) => {
    const today = new Date();
    let filteredData = [];

    if (this.state.selectedOption === "last7Days") {
      filteredData = this.getLast7DaysData(tokenSales.daily, today);
    } else if (this.state.selectedOption === "allMonths") {
      filteredData = this.getAllMonthsData(tokenSales.monthly);
    } else if (this.state.selectedOption === "last7Weeks") {
      filteredData = this.getLast7WeeksData(tokenSales.weekly);
    }

    this.setState({ dataPoints: filteredData });
  };

  // Filter data for the last 7 days
  getLast7DaysData = (dailySales, today) => {
    const last7Days = [];
    const salesByDate = new Map(
      dailySales.map((item) => [item.date, Number(item.totalTokens) || 0]),
    );

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i); // Calculate the date for each day in the last 7 days
      const dateKey = date.toISOString().slice(0, 10);

      last7Days.push({ x: date, y: salesByDate.get(dateKey) || 0 });
    }

    return last7Days;
  };

  // Filter data for all months
  getAllMonthsData = (monthlySales) => {
    const latestYear = monthlySales.reduce((latestMonthYear, item) => {
      const year = Number(item.month?.split("-")[0]);
      return year && year > latestMonthYear ? year : latestMonthYear;
    }, 0);

    return monthlySales
      .filter((item) => !latestYear || Number(item.month?.split("-")[0]) === latestYear)
      .map((item) => ({
      x: new Date(`${item.month}-01T00:00:00`),
      y: Number(item.totalTokens) || 0,
      label: this.formatMonthLabel(item.month),
    }));
  };

  formatMonthLabel = (monthKey) => {
    if (!monthKey) return "";
    const [year, month] = monthKey.split("-").map(Number);
    if (!year || !month) return monthKey;

    return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  // Filter data for the last 7 weeks
  getLast7WeeksData = (weeklySales) => {
    return weeklySales.slice(-7).map((item) => ({
      x: new Date(item.date),
      y: Number(item.totalTokens) || 0,
    }));
  };

  // Handle dropdown change to update the selected option and chart data
  handleDropdownChange = (event) => {
    const selectedOption = event.target.value;
    this.setState({ selectedOption }, () => {
      this.processData(); // Reuse already-fetched aggregate data
    });
  };

  // Run initial fetch on component mount
  componentDidMount() {
    this.fetchData();
  }

  render() {
    const options = {
      animationEnabled: true,
      theme: "light2", // Set the default theme
      title: {
        text: "Token Sale Graph",
        fontColor: "#FFFFFF", // Set title color to white
      },
      backgroundColor: "black", // Set the background color to dark
      axisX: {
        valueFormatString:
          this.state.selectedOption === "last7Days"
            ? "DD MMM"
            : this.state.selectedOption === "last7Weeks"
              ? "DD MMM"
              : "MMM YYYY", // Dynamic X-Axis format
        labelFormatter:
          this.state.selectedOption === "allMonths"
            ? function(e) {
                return e.label || new Date(e.value).toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                });
              }
            : undefined,
        labelFontColor: "#B0B0B0", // Axis X labels in light grey
        lineColor: "#B0B0B0", // Axis line color in light grey
        tickColor: "#B0B0B0", // Tick marks color in light grey
      },
      axisY: {
        title: "Tokens",
        labelFontColor: "#B0B0B0", // Axis Y labels in light grey
        lineColor: "#B0B0B0", // Axis line color in light grey
        tickColor: "#B0B0B0", // Tick marks color in light grey
      },
      creditText: "", // Remove CanvasJS logo text
      creditHref: "", // Remove CanvasJS logo link
      data: [
        {
          type: "spline", // Line chart type
          yValueFormatString: "#,###", // Format the Y axis values
          lineColor: "#FFB000", // Line color set to yellow
          markerColor: "#FFB000", // Marker color set to yellow
          dataPoints: this.state.dataPoints, // Dynamically changing data
        },
      ],
    };

    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "black",
          borderRadius: "10px",
        }}
      >
        <select
          value={this.state.selectedOption}
          onChange={this.handleDropdownChange}
          className="dropdown"
          style={{
            padding: "10px",
            fontSize: "16px",
            borderRadius: "5px",
            backgroundColor: "#333",
            color: "#fff",
            border: "1px solid #666",
            cursor: "pointer",
            marginBottom: "20px", // Add space between the dropdown and the chart
          }}
        >
          <option value="last7Days">Last 7 Days</option>
          <option value="allMonths">All Months</option>
          <option value="last7Weeks">Last 7 Weeks</option>
        </select>

        <CanvasJSChart options={options} />
      </div>
    );
  }
}

export default TokenChart;
